import { useState, useRef, useEffect } from "react";
import Quagga from "@ericblade/quagga2";
import { Scanner } from "./scanner";
import { OutlinedSelect } from "../outlined-select";
import { Box, Typography, CircularProgress } from "@mui/material";

export const QuaggaComponent = ({
  onDetected,
}: {
  onDetected?: (code: string) => void;
}) => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]); // array of available cameras, as returned by Quagga.CameraAccess.enumerateVideoDevices()
  const [cameraId, setCameraId] = useState<string | undefined>(); // id of the active camera device
  const [cameraError, setCameraError] = useState(null); // error message from failing to access the camera
  const scannerRef = useRef(null); // reference to the scanner element in the DOM

  // at start, we need to get a list of the available cameras.  We can do that with Quagga.CameraAccess.enumerateVideoDevices.
  // HOWEVER, Android will not allow enumeration to occur unless the user has granted camera permissions to the app/page.
  // AS WELL, Android will not ask for permission until you actually try to USE the camera, just enumerating the devices is not enough to trigger the permission prompt.
  // THEREFORE, if we're going to be running in Android, we need to first call Quagga.CameraAccess.request() to trigger the permission prompt.
  // AND THEN, we need to call Quagga.CameraAccess.release() to release the camera so that it can be used by the scanner.
  // AND FINALLY, we can call Quagga.CameraAccess.enumerateVideoDevices() to get the list of cameras.

  // Normally, I would place this in an application level "initialization" event, but for this demo, I'm just going to put it in a useEffect() hook in the App component.

  useEffect(() => {
    const enableCamera = async () => {
      await Quagga.CameraAccess.request(null, {});
    };
    const disableCamera = async () => {
      await Quagga.CameraAccess.release();
    };
    const enumerateCameras = async () => {
      const cameras = await Quagga.CameraAccess.enumerateVideoDevices();
      return cameras;
    };
    enableCamera()
      .then(disableCamera)
      .then(enumerateCameras)
      .then((cameras) => setCameras(cameras))
      .then(() => Quagga.CameraAccess.disableTorch()) // disable torch at start, in case it was enabled before and we hot-reloaded
      .catch((err) => setCameraError(err));
    return () => disableCamera() as unknown as void;
  }, []);

  const cameraOptions = cameras.map((camera) => {
    return { value: camera.deviceId, label: camera.label || camera.deviceId };
  });

  return (
    <Box sx={{ "& video": { width: "100%" } }}>
      {cameraError ? (
        <Typography variant="body1">
          ERROR INITIALIZING CAMERA ${JSON.stringify(cameraError)} -- DO YOU
          HAVE PERMISSION?
        </Typography>
      ) : null}
      <Box p={1} pt={2}>
        {cameras.length === 0 ? (
          <Typography variant="body1">
            Enumerating Cameras, browser may be prompting for permissions
            beforehand
          </Typography>
        ) : (
          <OutlinedSelect
            sx={{ minWidth: 200, mr: 1 }}
            label="Camera Source"
            value={cameraId}
            onChange={(value) => setCameraId(value)}
            options={cameraOptions}
          />
        )}
      </Box>
      <div ref={scannerRef} style={{ position: "relative" }}>
        <canvas
          className="drawingBuffer"
          style={{
            position: "absolute",
            top: "0px",
            width: "100%",
          }}
          width="100%"
          height="480"
        />
        <CircularProgress
          size={48}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: "-24px",
            marginLeft: "-24px",
            zIndex: 100,
            display: cameras.length === 0 ? "block" : "none",
          }}
        />
        <Scanner
          scannerRef={scannerRef}
          cameraId={cameraId}
          onDetected={onDetected}
        />
      </div>
    </Box>
  );
};
